import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Form,
  FormInstance,
  DateSerialized,
  Checkbox,
  SelectBooleanInput
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { EditAwareDate } from '../date';

interface EpxDischargeData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  step?: number;
  dischargeDate?: string;
  isGranular?: boolean;
  isWound?: boolean;
  hasMedication?: boolean;
  hasInstruction?: boolean;
  hasFall?: boolean;
  hasAppointment?: boolean;
  hasAdditionalHelp?: boolean;
  shouldAskAll?: boolean;
  isBinary?: boolean;
}

type EpxDischargeDataRaw = DateSerialized<
  EpxDischargeData,
  'start' | 'end' | 'dischargeDate'
>;

const DATE_FORMAT = 'YYYY-MM-DD';

export const EpxDischargeDefaultData: EpxDischargeData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  dischargeDate: moment().subtract(1, 'day').format(DATE_FORMAT),
  end: moment().add(1, 'month').add(1, 'day').format(DATE_FORMAT),
  tz: GetCurrentTZ(),
  times: ['12:00'],
  days: [1, 4],
  isGranular: false,
  isWound: false,
  hasMedication: false,
  hasInstruction: false,
  hasFall: false,
  hasAppointment: false,
  hasAdditionalHelp: false,
  shouldAskAll: false,
  isBinary: false
};

const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxDischargeFormProps {
  setForm?: (form: FormInstance<EpxDischargeDataRaw>) => void;
  onSubmit?: (data: EpxDischargeData) => void;
  isDisabled?: boolean;
}

const EpxDischargeForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxDischargeFormProps): JSX.Element => {
  const serialize = (data: EpxDischargeDataRaw): EpxDischargeData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      dischargeDate: SerializeDate(data.dischargeDate, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT)
    };
  };
  const deserialize = (data: EpxDischargeData): EpxDischargeDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      dischargeDate: DeserializeDate(data.dischargeDate, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxDischargeDefaultData);
  const form = new FormInstance<EpxDischargeDataRaw, EpxDischargeData>(
    defaultData,
    {
      serialize,
      deserialize
    }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxDischargeData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={24}>
                    <EditAwareDate
                      label="Discharge Date"
                      name="dischargeDate"
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBreakout>
                <Row>
                  <Col sm={24}>
                    <Weekday
                      label="Preferred Days (Pick At Least 1, Preferably 2 Days of Week)"
                      minItems={1}
                      inline
                      abbreviated
                    />
                  </Col>
                </Row>
              </CardBreakout>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <Checkbox
                      label="Ask Detailed Questions"
                      name="isGranular"
                    />
                  </Col>
                  {props.values.isGranular ? (
                    <>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Wound Dressing"
                          name="isWound"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Medications"
                          name="hasMedication"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Discharge Instruction"
                          name="hasInstruction"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Falls/Stumbles"
                          name="hasFall"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Follow-up Appointment"
                          name="hasAppointment"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask About Additional Help"
                          name="hasAdditionalHelp"
                        />
                      </Col>
                      <Col sm={12}>
                        <Checkbox
                          label="Ask All Questions (Even If Worse Feeling)"
                          name="shouldAskAll"
                        />
                      </Col>
                    </>
                  ) : null}
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Binary Mode"
                      name="isBinary"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxDischargeForm;
